/**
 * CookieBanner.tsx
 * 
 * GDPR-compliant cookie consent banner.
 * Design: Editorial Minimalism — matches site palette (warm beige, deep burgundy, stone).
 * Behavior: Shown on first visit, consent stored in localStorage.
 * On "Accept": stores consent flag, hides banner.
 * On "Decline": stores decline flag, hides banner (no tracking cookies set).
 * On "Learn more": links to /privacy-policy.
 */

import { useState, useEffect } from "react";
import { Link } from "wouter";
import { X } from "lucide-react";

const STORAGE_KEY = "brasovgateway_cookie_consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) {
      // Small delay so banner doesn't flash on first paint
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(STORAGE_KEY, "declined");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 md:pb-6"
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
    >
      <div
        className="max-w-4xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 p-5 md:p-6 shadow-2xl"
        style={{
          backgroundColor: "oklch(0.15 0.02 60)",
          borderTop: "2px solid oklch(0.55 0.1 40)",
        }}
      >
        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm leading-relaxed" style={{ color: "oklch(0.88 0.02 60)" }}>
            We use cookies to improve your experience and analyse site traffic. By clicking{" "}
            <strong style={{ color: "oklch(0.95 0.02 60)" }}>Accept</strong>, you consent to our use of cookies.{" "}
            <Link
              href="/privacy-policy"
              className="underline underline-offset-2 hover:opacity-80 transition-opacity"
              style={{ color: "oklch(0.75 0.08 40)" }}
            >
              Privacy Policy
            </Link>
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={handleDecline}
            className="text-xs uppercase tracking-widest px-4 py-2 border transition-all hover:opacity-80"
            style={{
              color: "oklch(0.65 0.02 60)",
              borderColor: "oklch(0.35 0.02 60)",
            }}
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="text-xs uppercase tracking-widest px-5 py-2 font-semibold transition-all hover:opacity-90"
            style={{
              backgroundColor: "oklch(0.55 0.1 40)",
              color: "oklch(0.97 0.01 60)",
            }}
          >
            Accept
          </button>
          <button
            onClick={handleDecline}
            className="p-1 hover:opacity-70 transition-opacity ml-1"
            aria-label="Close"
            style={{ color: "oklch(0.55 0.02 60)" }}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
